import { useLogto } from '@logto/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import React, {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';

import { getErrorStatus } from '@/utils/apiErrors';
import {
  type LogtoAiRole,
  type LogtoOrgClaims,
  logtoRoleForOrganization,
  resolveLogtoRole,
} from '@/utils/logtoRoles';

import { apiClient } from '../api/client';
import type { User, LoginResponse, ApiError } from '../api/types';
import { Button } from '../components/ui/button';
import { isDevAuthMode, logtoConfig } from '../lib/logto';
import {
  setAuthTokenProvider,
  clearAuthTokenProvider,
  setAuthTokenRefresher,
  clearAuthTokenRefresher,
} from '../services/authTokenStore';
import { logoutCoordinator } from '../services/logoutCoordinator';
import {
  getAccessToken as getStoredAccessToken,
  removeAccessToken,
  removeRefreshToken,
  setAccessToken,
  setRefreshToken,
} from '../utils/tokenStore';

/**
 * Authentication Context for Admin Portal
 * Provides authentication state management and token handling
 */

const DEV_TENANT_ID = '00000000-0000-4000-8000-000000000001';
const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const decodeJWT = (token: string) => {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const base64Url = parts[1];
    const base64 = base64Url
      .replace(/-/g, '+')
      .replace(/_/g, '/')
      .padEnd(base64Url.length + ((4 - (base64Url.length % 4)) % 4), '=');
    const payload = JSON.parse(atob(base64));
    return payload;
  } catch {
    return null;
  }
};

const isExpired = (payload: { exp?: unknown } | null): boolean =>
  typeof payload?.exp === 'number' && payload.exp * 1000 <= Date.now();

const isLogtoPayload = (payload: { iss?: unknown } | null): boolean =>
  typeof payload?.iss === 'string' && payload.iss.includes('sso.sys9.co');

/**
 * Role for the organization a Logto session is working in. Decided from the
 * token when possible; otherwise the backend names the selected organization.
 * Any failure falls back to the least-privileged role, never to admin.
 */
const resolveLogtoUserRole = async (
  claims: LogtoOrgClaims,
  selectedTenantId: string
): Promise<LogtoAiRole> => {
  const local = resolveLogtoRole(claims, selectedTenantId);
  if (local) return local;
  try {
    const context = await apiClient.request<{ organization_id?: string }>(
      'GET',
      '/api/v1/auth/tenant-context',
      undefined,
      {
        'X-Tenant-ID': selectedTenantId,
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache',
      }
    );
    return context?.organization_id
      ? logtoRoleForOrganization(claims, context.organization_id)
      : 'viewer';
  } catch {
    return 'viewer';
  }
};

/**
 * Role for a legacy (non-Logto) session when the profile endpoint gives none:
 * the role the token was issued with, else the least-privileged one. Never
 * admin by default; the backend decides what the token may do anyway.
 */
const LEAST_PRIVILEGED_ROLE = 'viewer';
const legacyRoleFromClaims = (payload: {
  role?: unknown;
  roles?: unknown;
}): string => {
  if (typeof payload?.role === 'string' && payload.role) return payload.role;
  if (Array.isArray(payload?.roles) && typeof payload.roles[0] === 'string') {
    return payload.roles[0];
  }
  return LEAST_PRIVILEGED_ROLE;
};

interface VerifiedTenantContext {
  subject: string;
  organization_id: string;
  tenant_id: string;
}

async function confirmTenant(selector: string | null, subject?: string) {
  if (selector !== null && !UUID_REGEX.test(selector)) {
    throw new Error('Saved organization is invalid. Select an organization.');
  }
  const context = await apiClient.request<VerifiedTenantContext>(
    'GET',
    '/api/v1/auth/tenant-context',
    undefined,
    {
      ...(selector ? { 'X-Tenant-ID': selector } : {}),
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache',
    }
  );
  if (
    !context ||
    !subject ||
    context.subject !== subject ||
    typeof context.organization_id !== 'string' ||
    !context.organization_id ||
    typeof context.tenant_id !== 'string' ||
    !UUID_REGEX.test(context.tenant_id) ||
    (selector !== null && context.tenant_id !== selector)
  ) {
    throw new Error('Selected organization was not confirmed by the server');
  }
  return context;
}

function tenantErrorMessage(failure: unknown) {
  const status = getErrorStatus(failure);
  if (status === 403)
    return 'Organization access was denied. Select another organization.';
  if (status === 401)
    return 'Your session could not be verified. Retry or sign in again.';
  if (status === 503)
    return 'Organization verification is temporarily unavailable. Retry verification.';
  return failure instanceof Error
    ? failure.message
    : 'Organization verification failed. Retry verification.';
}

// Auth context interface
interface AuthContextType {
  user: User | null;
  token: string | null;
  tenantId: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  identityEpoch?: number;
  error: string | null;
  bootstrapFailed?: boolean;
  retryBootstrap?: () => void;
  login: (username: string, password: string) => Promise<LoginResponse>;
  signIn: (redirectUri: string) => Promise<void>;
  logout: () => Promise<void>;
  switchTenant: (targetTenantId: string) => Promise<void>;
  clearError: () => void;
}

// Create context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Auth provider props
interface AuthProviderProps {
  children: ReactNode;
}

// Auth provider component
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [bootstrapFailed, setBootstrapFailed] = useState(false);
  const [bootstrapAttempt, setBootstrapAttempt] = useState(0);

  const transition = useRef(0);
  const [identityEpoch, setIdentityEpoch] = useState(0);
  const switching = useRef(false);
  const logoutState = useSyncExternalStore(
    logoutCoordinator.subscribe,
    logoutCoordinator.getSnapshot,
    logoutCoordinator.getSnapshot
  );
  const logoutMutation = useMutation({
    mutationFn: (request: Promise<void>) => request,
    networkMode: 'always',
  });
  const queryClient = useQueryClient();
  // NOTE: getAccessToken is async — only ever `await` it inside an async
  // callback (see switchTenant below), never during render. See the comment on
  // the token provider effect for why.
  const {
    signIn: sdkSignIn,
    signOut,
    getAccessToken,
    getIdToken,
    getRefreshToken,
    isAuthenticated: isLogtoAuthed,
  } = useLogto();

  // Mirror `token` into a ref so the token provider below can read the current
  // value synchronously without being re-registered on every token change.
  const tokenRef = useRef<string | null>(null);
  tokenRef.current = token;

  const sdk = useRef({ getAccessToken, getIdToken });
  sdk.current = { getAccessToken, getIdToken };

  useEffect(() => {
    void logoutCoordinator.reconcile(
      window.location.pathname,
      getIdToken,
      getRefreshToken
    );
  }, [getIdToken, getRefreshToken]);

  const signIn = useCallback(
    async (redirectUri: string) => {
      if (logoutCoordinator.isBlocked()) return;
      await sdkSignIn(redirectUri);
    },
    [sdkSignIn]
  );

  // Register the token provider for non-React modules.
  //
  // The provider MUST be synchronous. It deliberately does NOT call Logto's
  // useLogto().getAccessToken(): that method returns a Promise *and* is wrapped
  // in the SDK's proxy() helper which calls setIsLoading() on LogtoProvider.
  // Since getAuthToken() is invoked during render by several components
  // (e.g. WebSocketWrapper), calling it produced two bugs:
  //   1. "Cannot update a component (LogtoProvider) while rendering a different
  //      component" — setState during another component's render.
  //   2. A rejected promise per call ("LogtoClientError: Not authenticated"),
  //      spamming the console on every render.
  // The Promise also meant the old string/`.access_token` checks never matched,
  // so this provider silently returned null and every caller fell through to
  // the localStorage fallback in getAuthToken().
  useEffect(() => {
    setAuthTokenProvider(() => tokenRef.current);
    return () => {
      transition.current += 1;
      clearAuthTokenProvider();
    };
  }, []);

  // Async refresher for 401-retry interceptors. We use getIdToken() to fetch
  // the token itself (NOT getAccessToken()'s return value) because the
  // backend auth middleware verifies the Logto ID token JWT, while
  // getAccessToken() returns an opaque reference token that fails signature
  // verification → would cause a 401 loop after refresh.
  //
  // getIdToken() on its own does NOT refresh anything — per the SDK source
  // (@logto/client BaseClient) it is a plain `storage.getItem('idToken')`
  // with no expiry check and no network call. Only getAccessToken() checks
  // expiry and performs the refresh_token grant; as a side effect of that
  // grant the SDK verifies and re-persists a fresh ID token from the token
  // response (see getAccessTokenByRefreshToken → setIdToken()). So we must
  // call getAccessToken() FIRST to trigger the actual refresh, then read the
  // now-rotated ID token via getIdToken(). Without this, an expired ID token
  // is never renewed no matter how long the tab stays open or how valid the
  // underlying refresh token still is (2026-09-16 401-on-create-site bug).
  // Only invoked from network-failure callbacks (never during render), so
  // the SDK's setIsLoading side effect is safe here. Side-effects (setToken,
  // setAccessToken, apiClient.setToken) keep React state, cookie/localStorage
  // fallback, and the legacy apiClient in sync with the fresh token.
  useEffect(() => {
    setAuthTokenRefresher(async () => {
      if (logoutCoordinator.isBlocked()) return null;
      const generation = transition.current;
      try {
        await logoutCoordinator.refresh(getAccessToken);
        const newToken = (await getIdToken()) ?? null;
        if (generation !== transition.current || logoutCoordinator.isBlocked())
          return null;
        if (newToken) {
          tokenRef.current = newToken;
          setToken(newToken);
          setAccessToken(newToken);
          apiClient.setToken(newToken);
        }
        return newToken;
      } catch {
        return null;
      }
    });
    return () => clearAuthTokenRefresher();
  }, [getAccessToken, getIdToken]);

  // Sync tokens when Logto SDK transitions from unauthenticated to authenticated.
  // This fires on (a) post-redirect after LogtoCallback sets the SDK session,
  // and (b) page reload where Logto rehydrates session from storage. Without
  // this effect, the only signal that flows into tokenStore is the localStorage
  // peek inside LogtoCallback, which is fragile across Logto SDK versions.
  useEffect(() => {
    if (!isLogtoAuthed || logoutCoordinator.isBlocked()) return;
    let cancelled = false;
    const generation = transition.current;
    (async () => {
      try {
        const idToken = await getIdToken();
        if (
          cancelled ||
          generation !== transition.current ||
          !idToken ||
          logoutCoordinator.isBlocked()
        )
          return;
        if (tokenRef.current) return;
        tokenRef.current = idToken;
        setToken(idToken);
        setAccessToken(idToken);
        apiClient.setToken(idToken);
      } catch (err) {
        console.warn('Failed to sync Logto idToken into tokenStore:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isLogtoAuthed, getIdToken]);

  const isAuthenticated =
    !!user &&
    !!token &&
    !!tenantId &&
    !isLoading &&
    logoutState.phase === 'open';

  const clearAuthState = useCallback(() => {
    removeAccessToken();
    removeRefreshToken();
    localStorage.removeItem('tenant_id');
    apiClient.setToken(null);
    tokenRef.current = null;
    setToken(null);
    setTenantId(null);
    setUser(null);
  }, []);

  // Only the existing explicit mock-auth mode uses a local fixture tenant.
  const applyMockTenantId = useCallback((accessToken: string) => {
    if (!isDevAuthMode()) return null;
    const payload = decodeJWT(accessToken);
    const mockTenant = UUID_REGEX.test(payload?.tenant_id ?? '')
      ? payload.tenant_id
      : DEV_TENANT_ID;
    localStorage.setItem('tenant_id', mockTenant);
    setTenantId(mockTenant);
    return mockTenant;
  }, []);

  // Bootstrap and tenant switches share a generation fence, including unmount
  // and StrictMode cleanup. Stored selectors never publish tenant readiness.
  useEffect(() => {
    let cancelled = false;
    const generation = transition.current;
    const isCurrent = () =>
      !cancelled &&
      generation === transition.current &&
      !logoutCoordinator.isBlocked();
    // Logto bootstrap: the saved selector (or the server default) must be
    // confirmed by the server before the tenant is published as ready.
    const bootstrapLogtoSession = async () => {
      let idToken = getStoredAccessToken();
      let payload = idToken ? decodeJWT(idToken) : null;
      if (!idToken && !isLogtoAuthed) return;
      if (bootstrapAttempt > 0 || !idToken || isExpired(payload)) {
        const refreshed = await logoutCoordinator.refresh(() =>
          sdk.current.getAccessToken()
        );
        if (!refreshed) {
          throw new Error(
            'Session verification is temporarily unavailable. Retry verification.'
          );
        }
        idToken = (await sdk.current.getIdToken()) ?? null;
        if (!isCurrent()) return;
        payload = idToken ? decodeJWT(idToken) : null;
      }
      if (
        !idToken ||
        typeof payload?.sub !== 'string' ||
        !payload.sub ||
        isExpired(payload)
      ) {
        clearAuthState();
        return;
      }
      tokenRef.current = idToken;
      setToken(idToken);
      setAccessToken(idToken);
      apiClient.setToken(idToken);
      setUser({
        id: payload.sub,
        username: payload.username || payload.email || 'unknown',
        email: payload.email || '',
        first_name: '',
        last_name: '',
        // Least privilege until the server names the organization; the
        // role is then taken from that organization only (never admin by
        // default).
        role: LEAST_PRIVILEGED_ROLE,
        status: 'active',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      });
      const selector = localStorage.getItem('tenant_id');
      const context = await confirmTenant(selector, payload.sub);
      if (!isCurrent()) return;
      const role = logtoRoleForOrganization(payload, context.organization_id);
      setUser(current => (current ? { ...current, role } : current));
      localStorage.setItem('tenant_id', context.tenant_id);
      setTenantId(context.tenant_id);
    };
    const initializeAuth = async () => {
      try {
        if (logoutCoordinator.isBlocked()) return;
        // Migration guard: detect stale `logto:<oldAppId>:*` keys from a
        // previous bundle build and wipe them. Without this, Logto SDK
        // sees the cached session and silently re-authenticates against the
        // old (possibly deleted) application, causing the redirect-to-/login
        // loop. This is the fix for the recurring "stuck on /login" bug.
        const currentAppId = logtoConfig.appId;
        const purgeStaleLogto = (store: Storage) => {
          for (let i = store.length - 1; i >= 0; i--) {
            const k = store.key(i);
            if (!k) continue;
            const match = k.match(/^logto:([^:]+)/);
            if (match && match[1] !== currentAppId) {
              store.removeItem(k);
            }
          }
        };
        purgeStaleLogto(localStorage);
        purgeStaleLogto(sessionStorage);

        if (!isDevAuthMode()) {
          await bootstrapLogtoSession();
          return;
        }

        const storedToken = getStoredAccessToken();
        if (storedToken) {
          // Guard against corrupted values from previous sessions
          // (e.g. literal "undefined"/"null" accidentally persisted)
          if (storedToken === 'undefined' || storedToken === 'null') {
            clearAuthState();
            return;
          }

          const jwtPayload = decodeJWT(storedToken);

          // Non-JWT (opaque) tokens are only trusted for `dev-token-*`
          // bootstraps and in dev-auth mode (MSW mock tokens like
          // 'mock-access-token' are opaque strings). In production builds
          // isDevAuthMode() is compile-time false, so opaque tokens are
          // still rejected there.
          if (
            !jwtPayload &&
            !storedToken.startsWith('dev-token-') &&
            !isDevAuthMode()
          ) {
            clearAuthState();
            return;
          }

          if (jwtPayload?.exp && jwtPayload.exp * 1000 < Date.now()) {
            // The stored access token has expired. The actual refresh lives
            // in the axios 401 interceptor (services/api.js:222) which
            // delegates to refreshAuthToken() (Logto SDK getIdToken()). When
            // a request later returns 401, the interceptor retries with a
            // fresh token and only then redirects to /login. So we no
            // longer pre-emptively call the legacy /api/v1/auth/refresh
            // endpoint here — it expects HS256 JWTs, not Logto OIDC
            // ID tokens, and always 401s under USE_LOGTO_AUTH=true.
            // See docs/ux/architect-review-knowledge-local-401-loop-2026-08-20.md
            // for context.
          }

          const initialTenantId = applyMockTenantId(storedToken);
          if (!initialTenantId) {
            return;
          }

          apiClient.setToken(storedToken);
          setToken(storedToken);

          // Try to get current user
          try {
            // For Logto JWTs, the user is NOT in the local DB, so /users/me
            // always returns 404. Skip that endpoint and decode the JWT
            // directly — saves a round trip and avoids 401 auto-clear. The
            // role comes from the token's organization_roles for the selected
            // organization, the same way the backend authorizes requests.
            if (isLogtoPayload(jwtPayload)) {
              const role = await resolveLogtoUserRole(
                jwtPayload,
                initialTenantId
              );
              setUser({
                id: jwtPayload.sub || 'unknown',
                username: jwtPayload.username || jwtPayload.email || 'unknown',
                email: jwtPayload.email || '',
                first_name: '',
                last_name: '',
                role,
                status: 'active',
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
              });
            } else {
              const currentUser = await apiClient.getCurrentUser();
              if (!isCurrent()) return;
              setUser(currentUser);
            }
          } catch (userErr) {
            // If user is not found (404), the token is orphaned - clear it
            if (getErrorStatus(userErr) === 404) {
              console.warn(
                'Current user not found (404), clearing orphaned token'
              );
              clearAuthState();
            } else if (jwtPayload) {
              // For other errors (unauthorized, etc), fall back to the JWT's
              // own claims rather than dropping the session.
              setUser({
                id: jwtPayload.sub || jwtPayload.user_id || 'unknown',
                username: jwtPayload.username || jwtPayload.email || 'unknown',
                email: jwtPayload.email || '',
                first_name: '',
                last_name: '',
                role: legacyRoleFromClaims(jwtPayload),
                status: 'active',
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
              });
            }
          }
        }
      } catch (failure) {
        if (isCurrent()) {
          setTenantId(null);
          setBootstrapFailed(true);
          setError(tenantErrorMessage(failure));
        }
      } finally {
        if (isCurrent()) setIsLoading(false);
      }
    };

    setIsLoading(true);
    setBootstrapFailed(false);
    setTenantId(null);
    setError(null);
    void initializeAuth();
    return () => {
      cancelled = true;
      transition.current += 1;
    };
  }, [
    applyMockTenantId,
    clearAuthState,
    isLogtoAuthed,
    bootstrapAttempt,
    logoutState.phase,
  ]);

  const retryBootstrap = useCallback(() => {
    if (logoutCoordinator.isBlocked()) return;
    setIsLoading(true);
    setBootstrapFailed(false);
    setError(null);
    setBootstrapAttempt(attempt => attempt + 1);
  }, []);

  const login = useCallback(
    async (username: string, password: string): Promise<LoginResponse> => {
      // Direct password login is a dev/mock-only convenience. Outside
      // dev-auth mode (see isDevAuthMode in src/lib/logto.ts) the behavior is
      // preserved verbatim: Logto SSO is the only way in.
      if (!isDevAuthMode()) {
        throw new Error(
          'Direct password login is disabled. Use signIn() from useLogto() to authenticate via SSO.'
        );
      }

      if (logoutCoordinator.isBlocked())
        throw new Error('Sign-out must finish before signing in.');

      setError(null);
      try {
        // Reuse the existing api layer (hits POST /api/v1/auth/login —
        // served by MSW in mock mode or a locally seeded backend).
        const response = await apiClient.login(username, password);
        if (logoutCoordinator.isBlocked())
          throw new Error('Sign-out interrupted sign-in.');
        const accessToken = response.access_token;
        if (!accessToken) {
          throw new Error('Login response did not include an access_token');
        }

        // Persist via the shared token store (cookie + localStorage) so the
        // initializeAuth bootstrap restores the session on reload.
        setAccessToken(accessToken);
        if (response.refresh_token) {
          setRefreshToken(response.refresh_token);
        }
        apiClient.setToken(accessToken);
        tokenRef.current = accessToken;
        setToken(accessToken);

        // Explicit mock authentication retains its local fixture tenant.
        applyMockTenantId(accessToken);

        // Populate user state: prefer the user object embedded in the login
        // response (mock mode includes one), else fetch the profile.
        const embeddedUser = (response as unknown as { user?: Partial<User> })
          .user;
        if (embeddedUser) {
          setUser({
            id: embeddedUser.id || response.user_id || 'unknown',
            username: embeddedUser.username || username,
            email: embeddedUser.email || '',
            first_name: embeddedUser.first_name || '',
            last_name: embeddedUser.last_name || '',
            role:
              embeddedUser.role ||
              embeddedUser.roles?.[0] ||
              legacyRoleFromClaims(decodeJWT(accessToken)),
            roles: embeddedUser.roles,
            status: embeddedUser.status || 'active',
            created_at: embeddedUser.created_at || new Date().toISOString(),
            updated_at: embeddedUser.updated_at || new Date().toISOString(),
          });
        } else {
          try {
            const profile = await apiClient.getCurrentUser();
            if (logoutCoordinator.isBlocked())
              throw new Error('Sign-out interrupted sign-in.');
            setUser(profile);
          } catch {
            if (logoutCoordinator.isBlocked())
              throw new Error('Sign-out interrupted sign-in.');
            // Profile endpoint unavailable — fall back to a minimal user so
            // isAuthenticated still flips true after a successful login.
            setUser({
              id: response.user_id || 'unknown',
              username,
              email: '',
              first_name: '',
              last_name: '',
              role: legacyRoleFromClaims(decodeJWT(accessToken)),
              status: 'active',
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            });
          }
        }

        return response;
      } catch (err) {
        const apiError = err as ApiError;
        setError(apiError.message || 'Login failed');
        throw err;
      }
    },
    [applyMockTenantId]
  );

  const logout = useCallback((): Promise<void> => {
    return logoutCoordinator.start(
      async () => {
        if (!isDevAuthMode()) await signOut(`${window.location.origin}/login`);
      },
      () => {
        const generation = ++transition.current;
        setIdentityEpoch(generation);
        setBootstrapFailed(false);
        setError(null);
        void queryClient.cancelQueries();
        queryClient.clear();
        const backendRequest = apiClient.logout();
        void logoutMutation
          .mutateAsync(backendRequest)
          .catch(err => console.warn('Logout API call failed:', err));
        clearAuthState();
      }
    );
  }, [logoutMutation, signOut, queryClient, clearAuthState]);

  const switchTenant = useCallback(
    async (targetTenantId: string): Promise<void> => {
      if (
        logoutCoordinator.isBlocked() ||
        !user ||
        !targetTenantId ||
        (targetTenantId === tenantId && !switching.current)
      )
        return;
      const previousToken = tokenRef.current;
      const generation = ++transition.current;
      switching.current = true;
      setIdentityEpoch(generation);
      setIsLoading(true);
      setError(null);
      await queryClient.cancelQueries();
      if (generation !== transition.current) return;
      queryClient.clear();
      try {
        await logoutCoordinator.refresh(getAccessToken);
        const idToken = await getIdToken();
        if (generation !== transition.current || logoutCoordinator.isBlocked())
          return;
        if (!idToken) throw new Error('Logto session unavailable');
        // Backend verifies the ID token and requested selector. An opaque access
        // token is never used as the backend identity.
        apiClient.setToken(idToken);
        tokenRef.current = idToken;
        setToken(idToken);
        setAccessToken(idToken);
        const context = await confirmTenant(targetTenantId, user?.id);
        if (generation !== transition.current) return;
        localStorage.setItem('tenant_id', context.tenant_id);
        setTenantId(context.tenant_id);
        const claims = decodeJWT(idToken);
        if (isLogtoPayload(claims)) {
          const role = logtoRoleForOrganization(
            claims,
            context.organization_id
          );
          setUser(current => (current ? { ...current, role } : current));
        }
      } catch (failure) {
        if (
          generation === transition.current &&
          !logoutCoordinator.isBlocked()
        ) {
          tokenRef.current = previousToken;
          setToken(previousToken);
          apiClient.setToken(previousToken);
          if (previousToken) setAccessToken(previousToken);
          else removeAccessToken();
          setError(tenantErrorMessage(failure));
        }
        throw failure;
      } finally {
        if (generation === transition.current) {
          switching.current = false;
          setIsLoading(false);
        }
      }
    },
    [queryClient, tenantId, getAccessToken, getIdToken, user]
  );

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const value: AuthContextType = React.useMemo(
    () => ({
      user,
      token,
      tenantId,
      isAuthenticated,
      identityEpoch,
      isLoading,
      error,
      bootstrapFailed,
      retryBootstrap,
      login,
      signIn,
      logout,
      switchTenant,
      clearError,
    }),
    [
      user,
      token,
      tenantId,
      isAuthenticated,
      identityEpoch,
      isLoading,
      error,
      bootstrapFailed,
      retryBootstrap,
      login,
      signIn,
      logout,
      switchTenant,
      clearError,
    ]
  );

  return (
    <AuthContext.Provider value={value}>
      {logoutState.phase !== 'open' ? (
        <main className='mx-auto max-w-md space-y-4 p-6'>
          <h1 className='text-2xl font-semibold tracking-tight'>Signing out</h1>
          <p role='status'>
            {logoutState.phase === 'pending'
              ? 'You are signed out locally. Finishing provider sign-out...'
              : 'You are signed out locally. Provider sign-out has not left this page.'}
          </p>
          {logoutState.persistenceError && (
            <p role='alert'>
              Sign-out protection could not be saved. Reload may not preserve
              it.
            </p>
          )}
          <Button
            disabled={logoutState.phase === 'pending'}
            onClick={() => void logout()}
          >
            Retry sign-out
          </Button>
          <Button
            variant='outline'
            disabled={logoutState.persistenceError}
            onClick={() => window.location.reload()}
          >
            Reload to recover
          </Button>
        </main>
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
};

// Hook to use auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    console.error('useAuth must be used within an AuthProvider');
    return {
      user: null,
      token: null,
      tenantId: null,
      isAuthenticated: false,
      isLoading: true,
      error: 'Auth context not available',
      login: async () => {
        throw new Error('Auth context not available');
      },
      signIn: async () => {
        throw new Error('Auth context not available');
      },
      logout: async () => {
        throw new Error('Auth context not available');
      },
      switchTenant: async () => {
        throw new Error('Auth context not available');
      },
      clearError: () => {},
    };
  }
  return context;
};

export default AuthContext;
